import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import DateRange from "./DateRange";
import DeepSearch from "./DeepSearch";
import Export from "./Export";
import { AppContext } from "../../App";
import { convertToUtc, ToFormData } from "../../hooks/Method";
import { Server } from "../../api/Server";
import dayjs from "dayjs";

function DynamicTable({
  enableExport = false,
  enableDateRange = false,
  showDefaultDate = false,
  defaultDate,
  headers = [],
  setRecords,
  records = [],
  HeaderComp = <div></div>,
  ActionComp = <div></div>,
  dynamicSearch = true,
  FooterRow,
  endpointKey,
  exportEndpoint,
  enableSelect = false,
  setResult,
}) {
  const maxSelect = enableSelect ? 100 : 0;
  const { authUser, setLoader, selStore } = useContext(AppContext);
  const [pageNum, setPageNum] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchVal, setSearchVal] = useState("");
  const [totalRecord, setTotalRecord] = useState(0);
  const [sortDate, setSortDate] = useState({
    fromDate: showDefaultDate
      ? defaultDate?.fromDate || dayjs().startOf("month")
      : "",
    toDate: showDefaultDate
      ? defaultDate?.toDate || dayjs().endOf("month")
      : "",
  });

  const param = {
    id: selStore,
    pageNum,
    rowsPerPage,
    searchVal: searchVal || "",
    fromDate: sortDate.fromDate ? new Date(sortDate.fromDate) : "",
    toDate: sortDate.toDate ? new Date(sortDate.toDate) : "",
    today: new Date(),
  };

  const fetchRecords = () => {
    setLoader(true);
    Server(endpointKey).post(
      ToFormData({
        ...param,
        fromDate: convertToUtc(param.fromDate),
        toDate: convertToUtc(param.toDate),
        today: convertToUtc(param.today),
        maxSelect,
      }),
      (res) => {
        if (res && res.status === 200) {
          setRecords([...res.result]);
          setTotalRecord(res.totalRecord || 0);
          setResult && setResult({ ...res });
        } else setRecords([]);
        setLoader(false);
      }
    );
  };

  useEffect(() => {
    authUser && fetchRecords(0);
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, pageNum, rowsPerPage, sortDate, endpointKey, selStore]);

  useEffect(() => {
    if (searchVal.length === 0 && authUser) {
      fetchRecords(0);
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchVal]);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPageNum(0);
  };

  const handleChangePage = (event, newPage) => {
    setPageNum(newPage);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    authUser && fetchRecords(0);
  };

  return (
    <>
      <Box className="d-flex j-between a-center flex-wrap mb-15 g-5 bg-white p-5 br-15">
        <Box className="d-flex" sx={{ width: { xs: "100%", md: "auto" } }}>
          <HeaderComp />
        </Box>
        {dynamicSearch && (
          <Box
            sx={{ width: { md: "auto", xs: "100%" } }}
            className="d-flex flex-wrap g-5"
          >
            {enableExport && <Export endpoint={exportEndpoint} value={param} />}
            {enableDateRange && <DateRange {...{ sortDate, setSortDate }} />}
            <DeepSearch {...{ searchVal, setSearchVal, handleSearch }} />
          </Box>
        )}
      </Box>

      {/* Table  */}
      <Paper
        sx={{
          width: "auto",
          overflow: "hidden",
        }}
      >
        <TableContainer sx={{ maxHeight: "calc(100vh - 184px)" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {headers.map((item, i) => (
                  <TableCell
                    key={i}
                    sx={{
                      fontSize: "14px",
                      backgroundColor: "var(--white-color)",
                      color: "var(--primary-color)",
                      borderBottom: "1px solid var(--primaryMD-color)",
                      minWidth: "80px",
                      textTransform: "uppercase",
                      padding: "16px 8px 16px 16px",
                    }}
                  >
                    {item.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {records &&
                records.map((item, i) => {
                  return (
                    <TableRow hover key={i}>
                      {headers.map((headItem, index) => {
                        return (
                          <TableCell
                            key={index}
                            sx={{
                              fontSize: "16px",
                              padding: "6px 16px 6px 16px",
                              whiteSpace: "nowrap",
                              color: "var(--gray-color)",
                            }}
                          >
                            {headItem.id === "actions" ? (
                              <ActionComp {...item} />
                            ) : (
                              item[headItem.id]
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}

              {FooterRow && <TableRow>{<FooterRow />}</TableRow>}
            </TableBody>
          </Table>

          {!records.length && (
            <Typography
              variant="h6"
              className="tx-center p-30 tx-upp gray-color"
            >
              {totalRecord === null
                ? "Loading..."
                : totalRecord === 0 && "Data not avaiable"}
            </Typography>
          )}
        </TableContainer>
        {totalRecord > 0 && (
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            labelRowsPerPage="Row"
            count={totalRecord}
            rowsPerPage={rowsPerPage}
            page={pageNum}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>
    </>
  );
}

export default DynamicTable;
